import React, { useEffect, useState } from 'react';
import {
  Box, IconButton, Tooltip,
} from '@mui/material';
import searchIcon from 'Images/search_icon.svg';
import searchIcon2 from 'Images/search_icon2.svg';
import { SearchResult } from 'Shared/Layout/SubHeader';
import { searchAutocomplete } from 'Components/Catalog/SearchService';
import { getProductsData } from 'Components/Catalog/CatalogServices';
import useDebounce from 'hooks/useDebounce';
import { useHistory, useLocation } from 'react-router-dom';

const SearchBar = () => {
  const history = useHistory();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchAnchorRef = React.useRef(null);
  const debounceSearch = useDebounce(searchValue, 600);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('search');
    if (q) {
      setSearchValue(q);
    } else {
      setSearchValue('');
      setSearchOpen(false);
    }
  }, [location.pathname, location.search]);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value) {
      setSearchOpen(true);
      setSearchLoading(true);
    } else {
      setSearchOpen(false);
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const fetchSearch = async () => {
      if (!searchOpen || !debounceSearch || debounceSearch.length < 2) return;
      setSearchLoading(true);
      try {
        const results = await searchAutocomplete(debounceSearch);
        if (results?.length > 0) {
          setSearchProducts(results.map((p) => ({
            id: p.id,
            reference: p.reference,
            name: p.name,
            origin: p.origin,
            price: p.price,
            oldPrice: p.oldPrice,
            remise: p.remise,
            remiseExceptionnelle: p.remiseExceptionnelle,
            stockBouskoura: p.stockBouskoura,
            stockDerbOmar: p.stockDerbOmar,
            stockAinSebaa: p.stockAinSebaa,
            stockCfcim: p.stockCfcim,
            promo: p.promo,
            isNew: p.isNew,
            image: p.image || null,
            images: p.images,
            brand: p.brandName ? { name: p.brandName, logoImage: p.brandLogoImage } : undefined,
            family: p.familyName ? { name: p.familyName } : undefined,
          })));
        } else {
          throw new Error('empty');
        }
      } catch (_) {
        try {
          const res = await getProductsData('page=0', { search: debounceSearch });
          setSearchProducts((res?.content || []).slice(0, 6).map((p) => ({
            id: p.id,
            reference: p.reference,
            name: p.name,
            image: p.image,
            brand: p.brand,
            family: p.family,
          })));
        } catch (__) { /* silent */ }
      } finally {
        setSearchLoading(false);
      }
    };
    fetchSearch();
  }, [debounceSearch, searchOpen]);

  return (
    <>
      {/* Desktop search bar */}
      <Box
        ref={searchAnchorRef}
        sx={{
          flex: 1,
          height: '48px',
          background: '#F8FAFC',
          border: '1px solid #DEE5EE',
          borderRadius: '8px',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          pl: '12px',
          pr: '8px',
          gap: '6px',
        }}
      >
        <img
          src={searchIcon}
          alt="search"
          style={{
            width: 20, height: 20, flexShrink: 0, objectFit: 'contain',
          }}
        />
        <input
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={() => searchValue && setSearchOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchValue.trim()) {
              setSearchOpen(false);
              e.target.blur();
              history.push(`/catalog?search=${encodeURIComponent(searchValue.trim())}`, { searchVersion: Date.now() });
            }
          }}
          placeholder="Rechercher..."
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 14,
            color: '#4D4D4D',
          }}
        />
        <Box
          onClick={() => {
            if (searchValue.trim()) {
              setSearchOpen(false);
              history.push(`/catalog?search=${encodeURIComponent(searchValue.trim())}`, { searchVersion: Date.now() });
            }
          }}
          sx={{
            borderRadius: '10px',
            backgroundColor: '#007BC6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            p: '8px',
            '&:hover': { backgroundColor: '#005F9E' },
          }}
        >
          <img
            src={searchIcon2}
            alt="search"
            style={{
              width: 14,
              height: 14,
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>

      <SearchResult
        products={searchProducts}
        open={searchOpen}
        setOpen={setSearchOpen}
        anchorRef={searchAnchorRef}
        isHeader
        loading={searchLoading}
      />

      {/* Mobile search icon */}
      <Tooltip title="Recherche">
        <IconButton
          onClick={() => setSearchOpen(true)}
          sx={{ color: 'gray', display: { xs: 'flex', md: 'none' } }}
        >
          <img src={searchIcon} alt="search" style={{ width: 20, height: 20, objectFit: 'contain' }} />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default SearchBar;
